/**
 * Section Visibility System
 * Centralized utility for determining which resume sections should be rendered.
 * Used across preview, PDF export, DOCX export, ATS output, and print layouts.
 */

/**
 * All supported resume section keys
 */
export const SECTION_KEYS = [
  'summary',
  'experience',
  'education',
  'skills',
  'projects',
  'certifications',
  'languages',
  'achievements'
];

/**
 * Section metadata for UI display
 */
export const SECTION_META = {
  summary: { label: 'Professional Summary', icon: '📝', order: 0 },
  experience: { label: 'Work Experience', icon: '💼', order: 1 },
  education: { label: 'Education', icon: '🎓', order: 2 },
  skills: { label: 'Skills', icon: '⚡', order: 3 },
  projects: { label: 'Projects', icon: '🚀', order: 4 },
  certifications: { label: 'Certifications', icon: '🏆', order: 5 },
  languages: { label: 'Languages', icon: '🌐', order: 6 },
  achievements: { label: 'Achievements', icon: '🏅', order: 7 }
};

/**
 * Check if a string has meaningful content
 * Handles empty strings, whitespace-only, and empty HTML blocks
 */
const hasStringContent = (value) => {
  if (!value) return false;
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  // Check for empty rich text content
  const emptyHtmlPatterns = [
    '<p><br></p>',
    '<p></p>',
    '<br>',
    '<br/>',
    '<p>&nbsp;</p>',
    '&nbsp;'
  ];
  if (emptyHtmlPatterns.includes(trimmed)) return false;
  // Check for HTML that only contains whitespace/empty tags
  const stripped = trimmed.replace(/<[^>]*>/g, '').trim();
  return stripped.length > 0;
};

/**
 * Check if an array has meaningful data (non-empty items)
 */
const hasArrayContent = (arr) => {
  if (!Array.isArray(arr)) return false;
  if (arr.length === 0) return false;
  // Check if at least one item has content
  return arr.some(item => {
    if (typeof item === 'string') return hasStringContent(item);
    if (typeof item === 'object' && item !== null) {
      return Object.values(item).some(v => {
        if (typeof v === 'string') return hasStringContent(v);
        if (Array.isArray(v)) return v.length > 0;
        return !!v;
      });
    }
    return !!item;
  });
};

/**
 * Determines whether a resume section should be rendered.
 * 
 * @param {string} sectionKey - The section identifier (e.g., 'experience', 'skills')
 * @param {*} sectionData - The data for that section (array, string, or object)
 * @param {string[]} hiddenSections - Array of section keys the user has explicitly hidden
 * @returns {boolean} Whether the section should be rendered
 * 
 * @example
 * shouldRenderSection('experience', resumeData.experiences, hiddenSections)
 * shouldRenderSection('summary', resumeData.summary, hiddenSections)
 */
export const shouldRenderSection = (sectionKey, sectionData, hiddenSections = []) => {
  // Check if explicitly hidden by user
  if (Array.isArray(hiddenSections) && hiddenSections.includes(sectionKey)) {
    return false;
  }

  // Check based on data type
  if (sectionData === null || sectionData === undefined) {
    return false;
  }

  // String content (e.g., summary)
  if (typeof sectionData === 'string') {
    return hasStringContent(sectionData);
  }

  // Array content (e.g., experiences, skills)
  if (Array.isArray(sectionData)) {
    return hasArrayContent(sectionData);
  }

  // Object content
  if (typeof sectionData === 'object') {
    return Object.values(sectionData).some(v => {
      if (typeof v === 'string') return hasStringContent(v);
      if (Array.isArray(v)) return hasArrayContent(v);
      return !!v;
    });
  }

  return !!sectionData;
};

/**
 * Resolves section data from resume data for a given section key.
 * Maps section keys to the actual data fields in resumeData.
 * 
 * @param {string} sectionKey - Section identifier
 * @param {object} resumeData - Full resume data object
 * @returns {*} The data associated with that section
 */
export const getSectionData = (sectionKey, resumeData) => {
  if (!resumeData) return null;

  switch (sectionKey) {
    case 'summary':
      return resumeData.summary;
    case 'experience':
      return resumeData.experiences || resumeData.experience || [];
    case 'education':
      return resumeData.education || [];
    case 'skills':
      // Skills section is composite: technicalSkills + softSkills + tools
      const techSkills = resumeData.technicalSkills || resumeData.skills || [];
      const softSkills = resumeData.softSkills || [];
      const tools = resumeData.tools || [];
      const combined = [...techSkills, ...softSkills, ...tools];
      return combined;
    case 'projects':
      return resumeData.projects || [];
    case 'certifications':
      return resumeData.certifications || [];
    case 'languages':
      return resumeData.languages || [];
    case 'achievements':
      return resumeData.achievements || [];
    default:
      return resumeData[sectionKey];
  }
};

/**
 * Returns the full visibility map for all sections.
 * 
 * @param {object} resumeData - Full resume data object
 * @param {string[]} hiddenSections - User-hidden sections
 * @returns {object} Map of sectionKey -> boolean (visible or not)
 */
export const getSectionVisibilityMap = (resumeData, hiddenSections = []) => {
  const map = {};
  for (const key of SECTION_KEYS) {
    const data = getSectionData(key, resumeData);
    map[key] = shouldRenderSection(key, data, hiddenSections);
  }
  return map;
};

/**
 * Applies hidden sections to resume data by nullifying hidden section data.
 * This ensures that even if a template doesn't use {{#hasXxx}} conditionals,
 * the section loop {{#experiences}}...{{/experiences}} will still produce no output.
 * 
 * @param {object} resumeData - Original resume data
 * @param {string[]} hiddenSections - Sections to hide
 * @returns {object} Modified resume data with hidden sections emptied
 */
export const applyHiddenSections = (resumeData, hiddenSections = []) => {
  if (!hiddenSections || hiddenSections.length === 0) return resumeData;

  const modified = { ...resumeData };

  for (const sectionKey of hiddenSections) {
    switch (sectionKey) {
      case 'summary':
        modified.summary = '';
        break;
      case 'experience':
        modified.experiences = [];
        modified.experience = [];
        break;
      case 'education':
        modified.education = [];
        break;
      case 'skills':
        modified.technicalSkills = [];
        modified.softSkills = [];
        modified.tools = [];
        modified.skills = [];
        break;
      case 'projects':
        modified.projects = [];
        break;
      case 'certifications':
        modified.certifications = [];
        break;
      case 'languages':
        modified.languages = [];
        break;
      case 'achievements':
        modified.achievements = [];
        break;
      default:
        modified[sectionKey] = Array.isArray(modified[sectionKey]) ? [] : '';
    }
  }

  return modified;
};

/**
 * JavaScript snippet injected into rendered template HTML.
 * Runs in the iframe/preview context to remove empty section containers
 * and orphaned headings that have no content following them.
 * 
 * This handles:
 * 1. Templates with <section> / .section wrappers (silver templates, gold 60+)
 * 2. Templates with body-level <h2> headings without wrappers (gold 35-59)
 * 
 * Rule: A section header is hidden ONLY if its corresponding content is
 * completely empty (no text, no images). If content exists → header stays visible.
 */
export const EMPTY_SECTION_CLEANUP_SCRIPT = `
<script id="rg-section-cleanup">
(function() {
  // Guard against double-execution
  if (window.__rgCleanupDone) return;
  window.__rgCleanupDone = true;

  // Check whether an element has any real visible content
  function hasRealContent(el) {
    if (!el) return false;
    if (el.style.display === 'none') return false;
    if (el.getAttribute('data-rg-hidden')) return false;
    // Visual content (images, svg, canvas)
    if (el.querySelector('img, svg, canvas, video, iframe')) return true;
    var text = (el.textContent || '').trim();
    return text.length > 0;
  }

  // Check if an element is a section heading (H1-H6 tag only)
  // We do NOT match by class name — that caused false positives with
  // .job-title, .subtitle, .title-center etc.
  function isHeadingTag(el) {
    return /^H[1-6]$/.test(el.tagName);
  }

  // Check if element is a section-title div (used in some templates as headings)
  function isSectionTitleElement(el) {
    if (isHeadingTag(el)) return true;
    // Only exact class matches for known section-title patterns
    if (el.classList.contains('section-title')) return true;
    if (el.classList.contains('sidebar-title')) return true;
    if (el.classList.contains('section-heading')) return true;
    return false;
  }

  // ═══════════════════════════════════════════════════════════════
  // PART 1: Handle <section> / .section container-based templates
  // ═══════════════════════════════════════════════════════════════
  function cleanupContainerSections() {
    var sections = document.querySelectorAll(
      'section, .section, .sidebar-section'
    );
    sections.forEach(function(section) {
      if (section.getAttribute('data-rg-hidden')) return;
      var children = Array.from(section.children);
      if (children.length === 0 && (section.textContent || '').trim().length === 0) {
        section.style.display = 'none';
        section.setAttribute('data-rg-hidden', 'empty');
        return;
      }
      // Separate title elements from content elements
      var contentChildren = children.filter(function(c) {
        return !isSectionTitleElement(c) && c.tagName !== 'HR';
      });
      // If there are NO content children (only headings/HRs), it's empty
      if (contentChildren.length === 0) {
        section.style.display = 'none';
        section.setAttribute('data-rg-hidden', 'empty');
        return;
      }
      // If ALL content children are empty/hidden, it's empty
      var allEmpty = contentChildren.every(function(c) { return !hasRealContent(c); });
      if (allEmpty) {
        section.style.display = 'none';
        section.setAttribute('data-rg-hidden', 'empty');
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // PART 2: Handle body-level orphaned headings (gold 35-59 style)
  // These templates have <h2> directly in body with content siblings
  // between them (not wrapped in section containers).
  // ═══════════════════════════════════════════════════════════════
  function cleanupOrphanedHeadings() {
    // Only process H2 elements (section-level headings)
    var allH2 = document.querySelectorAll('h2');
    
    allH2.forEach(function(h2) {
      // Skip if already hidden
      if (h2.style.display === 'none' || h2.getAttribute('data-rg-hidden')) return;
      
      // Skip if inside a section/container that was already processed
      var parent = h2.parentElement;
      if (!parent) return;
      // Only process headings whose parent is body or a top-level wrapper
      // (not inside .section, section, .sidebar-section, .header-bar, header, etc.)
      var parentTag = parent.tagName.toLowerCase();
      var parentClass = (parent.className || '').toLowerCase();
      var isTopLevel = parentTag === 'body' || 
                       parentClass.includes('main-content') ||
                       parentClass.includes('content-area') ||
                       parentClass.includes('resume-content') ||
                       (parentTag === 'div' && parent.parentElement && parent.parentElement.tagName === 'BODY');
      
      if (!isTopLevel) return;
      
      // Skip headings inside containers that were hidden
      if (parent.getAttribute('data-rg-hidden')) return;
      
      // Walk nextElementSiblings until we hit another H2 (or H1) or end
      var sibling = h2.nextElementSibling;
      var hasContent = false;
      var betweenElements = [];
      
      while (sibling) {
        // Stop at the next section-level heading
        if (sibling.tagName === 'H2' || sibling.tagName === 'H1') break;
        
        betweenElements.push(sibling);
        
        // Skip already-hidden elements and HRs
        if (sibling.style.display === 'none' || sibling.getAttribute('data-rg-hidden')) {
          sibling = sibling.nextElementSibling;
          continue;
        }
        if (sibling.tagName === 'HR') {
          sibling = sibling.nextElementSibling;
          continue;
        }
        
        // Check if this sibling has real content
        if (hasRealContent(sibling)) {
          hasContent = true;
          break;
        }
        
        sibling = sibling.nextElementSibling;
      }
      
      // If NO content found between this H2 and the next H2 → hide this heading
      if (!hasContent) {
        h2.style.display = 'none';
        h2.setAttribute('data-rg-hidden', 'empty');
        // Also hide empty elements between this heading and the next
        betweenElements.forEach(function(el) {
          if (!hasRealContent(el) && !el.getAttribute('data-rg-hidden')) {
            el.style.display = 'none';
            el.setAttribute('data-rg-hidden', 'empty');
          }
        });
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // PART 3: Cleanup orphan HRs adjacent to hidden elements
  // ═══════════════════════════════════════════════════════════════
  function cleanupOrphanHRs() {
    var hrs = document.querySelectorAll('hr');
    hrs.forEach(function(hr) {
      var prev = hr.previousElementSibling;
      var next = hr.nextElementSibling;
      var prevHidden = prev && (prev.style.display === 'none' || prev.getAttribute('data-rg-hidden'));
      var nextHidden = next && (next.style.display === 'none' || next.getAttribute('data-rg-hidden'));
      if ((prevHidden && nextHidden) || (!prev && nextHidden) || (prevHidden && !next)) {
        hr.style.display = 'none';
      }
    });
  }

  function runCleanup() {
    cleanupContainerSections();  // Part 1: section containers
    cleanupOrphanedHeadings();   // Part 2: body-level headings
    cleanupOrphanHRs();          // Part 3: cosmetic HR cleanup
  }

  // Run cleanup once after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(runCleanup, 50);
    });
  } else {
    setTimeout(runCleanup, 50);
  }
})();
</script>
`;
